import { useEffect, useState } from 'react';

import { DEBOUNCE_MS } from '@/components/features/UsersList/constants';
import BaseMultiAutocomplete from '@/components/shared/Autocomplete/BaseMultiAutocomplete';
import { BaseMultiAutocompleteProps } from '@/components/shared/Autocomplete/types';
import { useOrganizationsAutocomplete } from '@/hooks/autocomplete/useOrganizationsAutocomplete';
import type { components } from '@/shared/api/openapi';

type Organization = components['schemas']['GetOrganizationsAutoCompleteEndpoint_Response'];

export interface OrganizationAutocompleteProps<Organization> {
  value: Organization[];
  onChange: BaseMultiAutocompleteProps<Organization>['autocomplete']['onChange'];
}

function OrganizationAutocomplete({ value, onChange }: OrganizationAutocompleteProps<Organization>) {
  const [inputValue, setInputValue] = useState('');
  const [debouncedValue, setDebouncedValue] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(inputValue);
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [inputValue]);

  const { data: organizations, isLoading } = useOrganizationsAutocomplete({
    filter: debouncedValue,
    enabled: debouncedValue.length >= 3,
  });

  // Use last fetched organizations if current is empty (e.g., when input is cleared)
  const displayOrganizations = (() => {
    if (organizations && organizations.length > 0) {
      return organizations;
    }
    return [];
  })();

  const handleInputChange = (_: React.SyntheticEvent, input: string) => {
    setInputValue(input);
  };

  const getOptionLabel = (option: Organization) => {
    return option.name || '';
  };

  const getOptionDisabled = (option: Organization) => {
    const isSelectedOptionDisabled = value.some((val) => val.id === option.id);

    return isSelectedOptionDisabled;
  };

  const isOptionEqualToValue = (option: Organization, value: Organization) => {
    return option.name?.toLowerCase().trim() === value.name?.toLowerCase().trim();
  };

  return (
    <BaseMultiAutocomplete<Organization>
      autocomplete={{
        options: displayOrganizations,
        value: value,
        loading: isLoading,
        onChange: onChange,
        onInputChange: handleInputChange,
        getOptionLabel: getOptionLabel,
        getOptionDisabled: getOptionDisabled,
        isOptionEqualToValue: isOptionEqualToValue,
        label: 'Filter by Client',
        placeholder: 'Enter at least 3 characters to search',
        testIdSuffix: 'clients',
      }}
    />
  );
}

export default OrganizationAutocomplete;
